/**
 * The third-place option, as a card — Pass 06 §3.
 *
 * ONE COMPONENT, THREE READERS. The GM who holds the right, the GM exposed to it, and the
 * commissioner recording a verbal answer all see the same card; what changes is which of them can
 * press the buttons and whose name is on the declaration. A second component for the commissioner
 * path is how the two drift, and the verbal path is the one that gets used on the night.
 *
 * THE PRIMARY BUTTON SWAPS SIDES. When 3rd already holds the better position the rule still offers
 * him the swap — and `swapPrompt` makes Keep the loud one. This declaration is binding and cannot
 * be undone; a technically correct card with the losing move styled as the obvious one is how that
 * becomes somebody's ruined draft.
 *
 * NOTHING IS DRAWN BEFORE THE DRAW. The option does not exist until `lotteryDrawnAt`, and a card
 * asking a man to choose between two positions neither of which has been drawn yet is worse than
 * no card at all.
 */

import { useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { bothZones } from '../domain/deadlines.js';
import { Citations } from './RuleText.js';
import { GmBadge } from './GmBadge.js';
import {
  counterpartNotice, swapPrompt, swapRecord, swapState, type SwapDeclaration, type SwapInput,
} from '../domain/thirdPlaceSwap.js';

interface Props {
  swap: SwapInput;
  /** The GM whose screen this is. */
  me: string;
  /** Commissioners may record a verbal declaration on 3rd's behalf. */
  canRecord: boolean;
  /** Who is actually operating the app — the commissioner's own id when he is recording. */
  actor: string;
  deadline: string | null;
  onDeclare: (d: SwapDeclaration) => void | Promise<void>;
}

export function ThirdPlaceSwap({ swap, me, canRecord, actor, deadline, onDeclare }: Props) {
  const [busy, setBusy] = useState(false);
  const state = swapState(swap);
  const third = (swap.thirdPlace ?? '').toUpperCase();
  const fourth = (swap.fourthPlace ?? '').toUpperCase();
  const up = me.toUpperCase();

  const isThird = up === third;
  const isFourth = up === fourth;
  /*
   * A commissioner sees the card so he can record an answer, but only while there is one to
   * record — after that it is a fact on the record and belongs in the record line below.
   */
  const recording = canRecord && !isThird && state === 'pending';

  const prompt = swapPrompt(swap, third ? managerShort(third) : undefined);
  const forFourth = counterpartNotice(swap, third ? managerShort(third) : undefined);

  /* Nobody else's business. The option is between two men, and the league sees it once settled. */
  if (!prompt || (!isThird && !isFourth && !recording)) return null;

  /* --------------------------------------------------------------- 4th, while it is live */
  if (isFourth && state === 'pending') {
    return (
      <section className="card tpswap exposed">
        <div className="striptop">
          <h3 className="striphead">Third-place option</h3>
          <span className="statepill amber">pending</span>
        </div>
        <p className="tpsline">{forFourth}</p>
        <p className="note">
          {/*
            The consequence, not just the fact. He cannot stop it and has no reciprocal right —
            saying so plainly is kinder than letting him go looking for a button.
          */}
          You have no say in it and no matching right of your own. Rank deep enough to cover
          both positions and it costs you nothing either way.
        </p>
        <Citations ids={['2018-II-5']} />
      </section>
    );
  }

  /* ------------------------------------------------------- settled: one line for the record */
  if (state !== 'pending') {
    return (
      <section className={`card tpswap ${state}`}>
        <div className="striptop">
          <h3 className="striphead">Third-place option</h3>
          <span className={`statepill ${state === 'taken' ? 'violet' : 'grey'}`}>{state}</span>
        </div>
        <p className="tpsline">{swapRecord(swap, (g) => managerShort(g))}</p>
        {swap.declaration?.at && (
          <p className="note">Declared {bothZones(swap.declaration.at)}.</p>
        )}
        <Citations ids={['2018-II-5']} />
      </section>
    );
  }

  /* ------------------------------------------------------------------ 3rd, or a commissioner */
  const declare = async (answer: 'taken' | 'declined') => {
    if (busy) return;
    setBusy(true);
    const now = new Date().toISOString();
    try {
      await onDeclare({
        answer,
        at: now,
        /*
         * ALWAYS THIRD PLACE'S NAME. A commissioner recording a verbal answer is a witness, not
         * the declarer — the same distinction the activity log draws between who acted and who
         * was acting. `recordedBy` is where he goes.
         */
        gm: third,
        declaredVia: recording ? 'verbal' : 'app',
        ...(recording ? { recordedBy: actor.toUpperCase(), recordedAt: now } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`card tpswap${prompt.wouldMoveBack ? ' back' : ''}`}>
      <div className="striptop">
        <h3 className="striphead">{prompt.headline}</h3>
        {recording && (
          <span className="statepill amber">Recording for {managerShort(third)}</span>
        )}
      </div>

      <p className="tpsbody">
        <GmBadge gm={third} />
        {prompt.body}
      </p>

      <div className="tpsrow">
        {/*
          Order follows importance, not outcome: the primary is always on the left of the pair and
          `swapPrompt` decides which action that is. Rendering them in a fixed take/keep order and
          restyling would put the loud button in a different place depending on the draw.
        */}
        <button type="button" disabled={busy}
          className={`btn ${prompt.take.primary ? 'primary' : ''}`}
          onClick={() => void declare('taken')}>
          {prompt.take.label}
        </button>
        <button type="button" disabled={busy}
          className={`btn ${prompt.keep.primary ? 'primary' : ''}`}
          onClick={() => void declare('declined')}>
          {prompt.keep.label}
        </button>
      </div>

      <p className="tpsnote">{prompt.note}</p>
      {deadline && (
        <p className="note">
          {/*
            SILENCE DECLINES, said on the card rather than only in the rulebook. It is the branch
            where he loses value he would almost always have taken, and the one nobody expects.
          */}
          No answer by {bothZones(deadline)} and the option lapses — the order resolves on your
          own draw.
        </p>
      )}
      <Citations ids={['2018-II-5']} />
    </section>
  );
}
